import { useMutation, useQuery } from '@apollo/client';
import { Button, Drawer, InputNumber, message } from 'antd';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ENABLE_CUSTOM_BAG_RULES } from '../constants';
import {
    CREATE_SORTING_BATCH_BAGS, CreateSortingBatchBagsResponse, CreateSortingBatchBagsVariables,
    GET_SORTING_CODES_BY_DESTINATION, GetSortingCodesByDestinationResponse, GetSortingCodesByDestinationVariables,
} from '../graphql/sortingBatch';
import { SortingBatchBagType } from '../graphql/sortingBatchDetail';
import { PrintJob } from '../types';
import {
    buildBagPlan, CodeRule, defaultRule, defaultSegFor, expandPlanCodes, MAX_COPIES_PER_CODE, normalizeCopies, segOptionIsSingle,
    segOptionsFor,
} from '../utils/sortCodeUtils';
import { CopiesStepper, CustomCodeRuleEditor, SelectableChip } from './CustomCodeRuleEditor';
import { CreateBagsDrawerProps, CreateBagsTarget } from './overlayProps';

/** 单个批次行的创建配置（原型 bagGrainBy / bagCustomBy / bagCopyBy / bagRules） */
interface RowConfig {
    seg: number;
    custom: boolean;
    copies: number;
    rules: CodeRule[];
}

const defaultRowConfig = (destination: string): RowConfig => ({
    seg: defaultSegFor(destination),
    custom: false,
    copies: 1,
    rules: [defaultRule(destination)],
});

const DRAWER_WIDTH = 'min(940px, 94vw)';

/** 合计文案中的数字高亮（各语言文案中只出现这一个数字） */
const highlightCount = (text: string, count: number): ReactNode => {
    const token = String(count);
    const at = text.indexOf(token);
    if (at < 0) return text;
    return (
        <>
            {text.slice(0, at)}
            <b className="font-medium text-ant-purple-6">{token}</b>
            {text.slice(at + token.length)}
        </>
    );
};

const toPrintJob = (bag: SortingBatchBagType, batch: CreateBagsTarget): PrintJob => ({
    bagId: bag.bagId,
    bagNumber: bag.bagNumber,
    sortingCode: bag.sortingCode || '',
    status: bag.status,
    batchNumber: batch.batchNumber,
    destination: batch.destination,
    copies: 1,
});

/** 创建包牌抽屉：为一个或多个批次按分拣码段数（或自定义规则）× 每码包牌数批量创建包牌 */
export const CreateBagsDrawer: React.FC<CreateBagsDrawerProps> = ({ open, batches: targets, onClose, onOpenPrint }) => {
    const { t } = useTranslation();
    const k = (key: string) => `wms.sortingBatch.createBags.${key}`;
    const [configs, setConfigs] = useState<Record<number, RowConfig>>({});
    const [bulkCopies, setBulkCopies] = useState(1);

    const batchIds = useMemo(() => targets.map((b) => String(b.id)), [targets]);
    const { data: codesData } = useQuery<GetSortingCodesByDestinationResponse, GetSortingCodesByDestinationVariables>(
        GET_SORTING_CODES_BY_DESTINATION,
        { variables: { batchIds }, skip: !open || !batchIds.length, fetchPolicy: 'network-only' },
    );
    const codesByBatchId = useMemo(() => {
        const map: Record<number, string[]> = {};
        (codesData?.sortingCodesByDestination || []).forEach((c) => { map[Number(c.batchId)] = c.codes; });
        return map;
    }, [codesData]);
    const allCodesFor = (b: CreateBagsTarget): string[] => codesByBatchId[b.id] || [];

    const [createSortingBatchBags, { loading: creating }] = useMutation<CreateSortingBatchBagsResponse, CreateSortingBatchBagsVariables>(
        CREATE_SORTING_BATCH_BAGS,
    );

    // 每次打开（含目标批次变化）重置为默认段数 / 份数
    const idsKey = batchIds.join(',');
    useEffect(() => {
        if (!open) return;
        setConfigs({});
        setBulkCopies(1);
    }, [open, idsKey]);

    const configOf = (b: CreateBagsTarget): RowConfig => configs[b.id] || defaultRowConfig(b.destination);

    const patchConfig = (b: CreateBagsTarget, patch: Partial<RowConfig>) =>
        setConfigs((prev) => ({ ...prev, [b.id]: { ...(prev[b.id] || defaultRowConfig(b.destination)), ...patch } }));

    const plans = targets.map((b) => {
        const config = configOf(b);
        return { batch: b, config, plan: buildBagPlan(b.destination, allCodesFor(b), config) };
    });
    const grandTotal = plans.reduce((sum, p) => sum + p.plan.total, 0);
    const separator = t('wms.sortingBatch.common.separator');

    const applyBulkCopies = () => {
        const copies = normalizeCopies(bulkCopies);
        setConfigs((prev) => {
            const next = { ...prev };
            targets.forEach((b) => {
                const c = next[b.id] || defaultRowConfig(b.destination);
                next[b.id] = { ...c, copies, rules: c.rules.map((r) => ({ ...r, copies })) };
            });
            return next;
        });
        setBulkCopies(copies);
        message.info(t(k('bulkApplied'), { count: targets.length, copies }));
    };

    /** 按当前配置为每个批次调用创建包牌接口；返回成功创建的打印任务，任一批次失败则整体不继续 */
    const createBags = async (): Promise<PrintJob[] | null> => {
        if (!grandTotal) {
            message.warning(t(k('nothingToCreate')));
            return null;
        }
        const requests = plans
            .filter((p) => p.plan.total > 0)
            .map((p) => ({ batch: p.batch, sortingCodes: expandPlanCodes(p.batch.destination, allCodesFor(p.batch), p.config) }));

        const results = await Promise.allSettled(
            requests.map((r) => createSortingBatchBags({ variables: { input: { batchId: String(r.batch.id), sortingCodes: r.sortingCodes } } })),
        );

        const failedBatchNames: string[] = [];
        const jobs: PrintJob[] = [];
        results.forEach((result, i) => {
            const { batch } = requests[i];
            if (result.status === 'fulfilled') {
                const bags = result.value.data?.createSortingBatchBags.bags || [];
                bags.forEach((bag) => jobs.push(toPrintJob(bag, batch)));
            } else {
                failedBatchNames.push(batch.batchNumber);
            }
        });
        if (failedBatchNames.length) {
            message.error(t(k('createFailed'), { names: failedBatchNames.join(separator) }));
            return null;
        }
        return jobs;
    };

    const handleCreateOnly = async () => {
        const jobs = await createBags();
        if (!jobs) return;
        message.success(t(k('created')));
        onClose();
    };

    const handleCreateAndPrint = async () => {
        const jobs = await createBags();
        if (jobs) onOpenPrint(jobs);
    };

    const renderRow = ({ batch, config, plan }: (typeof plans)[number]) => {
        const { custom } = config;
        const allCodes = allCodesFor(batch);
        return (
            <div key={batch.id}>
                <div className={`border-0 border-b border-solid border-[#f2f3f5] px-3 py-2 ${custom ? 'bg-[#faf7ff]' : 'bg-white'}`}>
                    <div className="flex items-start gap-1.5 whitespace-nowrap">
                        <span className="min-w-[110px] flex-[0_1_190px] self-center truncate text-[12.5px]" title={batch.batchNumber}>
                            {batch.batchNumber}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                            {!custom && segOptionsFor(batch.destination).map((n) => (
                                <SelectableChip key={n} selected={plan.seg === n} onClick={() => patchConfig(batch, { seg: n })}>
                                    {t(k(segOptionIsSingle(batch.destination, n, allCodes) ? 'seg' : 'segAll'), { n })}
                                </SelectableChip>
                            ))}
                            {/* 「自定义」规则延期（2026-10-14，plan §4.10），ENABLE_CUSTOM_BAG_RULES 为 false 时隐藏，代码保留 */}
                            {ENABLE_CUSTOM_BAG_RULES && (
                                <SelectableChip selected={custom} onClick={() => patchConfig(batch, { custom: !custom })}>
                                    {t(k(custom ? 'customOn' : 'custom'))}
                                </SelectableChip>
                            )}
                            {plan.codes.length > 0 && (
                                <>
                                    <span
                                        className="ml-2 min-w-[80px] flex-[1_1_80px] truncate font-mono text-[11.5px] leading-[17px] text-[#86909c]"
                                        title={plan.codes.join(separator)}
                                    >
                                        {plan.codes.join(separator)}
                                    </span>
                                    <span className="mr-4 flex-none text-[11.5px] text-[#86909c]">{t(k('codeCount'), { count: plan.codes.length })}</span>
                                </>
                            )}
                        </span>
                        {custom ? (
                            <span className="w-[86px] flex-none self-center text-center text-[11.5px] text-[#86909c]">{t(k('byRule'))}</span>
                        ) : (
                            <span className="flex w-[86px] flex-none justify-end">
                                <CopiesStepper value={config.copies} onChange={(copies) => patchConfig(batch, { copies })} />
                            </span>
                        )}
                        <span className="w-10 flex-none self-center text-right text-[12.5px] font-medium text-ant-purple-6" data-testid={`bag-row-total-${batch.id}`}>
                            {plan.total}
                        </span>
                    </div>
                </div>
                {custom && (
                    <CustomCodeRuleEditor
                        destination={batch.destination}
                        rules={config.rules}
                        allCodes={allCodes}
                        onChange={(rules) => patchConfig(batch, { rules })}
                    />
                )}
            </div>
        );
    };

    return (
        // destroyOnHidden：关闭后移除浮层容器、下次打开追加到末尾，否则会被之后重新打开的批次详情盖住；
        // 组件本身常驻，创建中的 loading 状态跨关闭保留，避免重开后重复提交
        <Drawer
            open={open}
            destroyOnHidden
            title={t(k('title'))}
            width={DRAWER_WIDTH}
            onClose={onClose}
            footer={(
                <div className="flex justify-end gap-2">
                    <Button onClick={onClose}>{t(k('cancel'))}</Button>
                    <Button type="primary" ghost loading={creating} onClick={handleCreateOnly}>{t(k('createOnly'))}</Button>
                    <Button type="primary" loading={creating} onClick={handleCreateAndPrint}>{t(k('createAndPrint'))}</Button>
                </div>
            )}
        >
            <div className="flex flex-col gap-3.5">
                <div className="flex items-center justify-end gap-2">
                    <span className="text-[12.5px] text-[#4e5969]">{t(k('bulkLabel'))}</span>
                    <InputNumber
                        size="small"
                        min={1}
                        max={MAX_COPIES_PER_CODE}
                        precision={0}
                        controls={false}
                        value={bulkCopies}
                        onChange={(v) => {
                            if (typeof v === 'number') setBulkCopies(v);
                        }}
                        className="!w-[52px] [&_input]:!text-center"
                    />
                    <Button size="small" type="primary" ghost onClick={applyBulkCopies}>{t(k('bulkApply'))}</Button>
                </div>
                <div className="rounded border border-solid border-[#e5e6eb]">
                    <div className="flex items-center gap-1.5 whitespace-nowrap border-0 border-b border-solid border-[#e5e6eb] bg-[#f7f8fa] px-3 py-[7px] text-[11.5px] font-medium text-[#4e5969]">
                        <span className="min-w-[110px] flex-[0_1_190px]">{t(k('colBatch'))}</span>
                        <span className="min-w-0 flex-1">{t(k('colCodes'))}</span>
                        <span className="w-[86px] flex-none">{t(k('colCopies'))}</span>
                        <span className="w-10 flex-none text-right">{t(k('colTotal'))}</span>
                    </div>
                    {plans.map(renderRow)}
                </div>
                <div className="rounded border border-solid border-[#f2f3f5] bg-[#f7f8fa] px-3.5 py-3 text-xs leading-[1.9] text-[#4e5969]">
                    {highlightCount(t(k('total'), { count: grandTotal }), grandTotal)}
                </div>
            </div>
        </Drawer>
    );
};
